/**
 * Authenticated calls to the relay's /admin/* endpoints. Uses a separate
 * secret from the branding PIN: the PIN only proves something to this
 * browser locally (see pin.ts) — it can't authorize real server-side
 * actions like viewing or kicking a session. See relay/activation.ts and
 * relay/index.ts for what these endpoints do.
 */

import type { LeaderboardEntry, LeaderboardPeriodInfo } from './leaderboardClient';

const OWNER_SECRET_KEY = 'mahjong-scorer:adminSecret';
const STAFF_SECRET_KEY = 'mahjong-scorer:staffSecret';

/**
 * The owner and the venue staff each keep their own secret on a device, under separate keys, so using the staff address on
 * the owner's phone never logs the owner out (and the other way round). Which one the admin calls use is decided once per page
 * load by the address the page was opened at: the staff address calls `useStaffSecret()` before anything else.
 */
let secretKey = OWNER_SECRET_KEY;
export function useStaffSecret(): void {
  secretKey = STAFF_SECRET_KEY;
}

// Same base-URL pattern as src/ui/activation.ts.
const RELAY_BASE = import.meta.env?.VITE_VISION_RELAY_URL ?? '/api';

export function getStoredAdminSecret(): string | undefined {
  return localStorage.getItem(secretKey) ?? undefined;
}

export function storeAdminSecret(secret: string): void {
  localStorage.setItem(secretKey, secret);
}

export function clearStoredAdminSecret(): void {
  localStorage.removeItem(secretKey);
}

export interface SessionInfo {
  id: string;
  issuedAt: number;
  deviceId: string;
  city?: string;
  country?: string;
  /** The registered venue this device was nearest to when it last checked in, and how far (metres, rounded up to 10). */
  nearestVenue?: string;
  nearestDistanceM?: number;
  venueName?: string;
  returnedAfterBlock?: boolean;
  tableId?: string;
}

/** Mirrors `src/ui/branding.ts`'s `Branding`. */
export interface Branding {
  name: string;
  logoDataUrl?: string;
  winnerMessage?: string;
}

export interface Venue {
  name: string;
  latitude: number;
  longitude: number;
  radiusMeters: number;
  /** Overrides the global default branding for any device that GPS-matches this venue. */
  branding?: Branding;
}

export class AdminAuthError extends Error {
  constructor() {
    super('Admin secret is missing or incorrect.');
    this.name = 'AdminAuthError';
  }
}

async function adminFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const secret = getStoredAdminSecret();
  if (!secret) throw new AdminAuthError();

  const res = await fetch(`${RELAY_BASE}${path}`, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${secret}` },
  });

  if (res.status === 401) {
    clearStoredAdminSecret();
    throw new AdminAuthError();
  }
  return res;
}

export async function fetchSessions(): Promise<SessionInfo[]> {
  const res = await adminFetch('/admin/sessions');
  const body = (await res.json()) as { sessions: SessionInfo[] };
  return body.sessions;
}

export async function fetchCurrentCode(): Promise<string> {
  const res = await adminFetch('/admin/code');
  const body = (await res.json()) as { code: string };
  return body.code;
}

export async function regenerateActivationCode(): Promise<string> {
  const res = await adminFetch('/admin/regenerate', { method: 'POST' });
  const body = (await res.json()) as { code: string };
  return body.code;
}

export async function fetchMaxScanAttempts(): Promise<number> {
  const res = await adminFetch('/admin/max-scan-attempts');
  const body = (await res.json()) as { maxScanAttempts: number };
  return body.maxScanAttempts;
}

/** Throws with the server's validation message on a rejected save (e.g. out of range). */
export async function saveMaxScanAttemptsApi(value: number): Promise<number> {
  const res = await adminFetch('/admin/max-scan-attempts', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ maxScanAttempts: value }),
  });
  const body = (await res.json()) as { maxScanAttempts: number } | { error: string };
  if (!res.ok) throw new Error('error' in body ? body.error : 'Could not save the scan limit.');
  return (body as { maxScanAttempts: number }).maxScanAttempts;
}

/** Who the stored secret belongs to: the owner (full admin) or venue staff (players and join requests only). */
export type AdminRole = 'owner' | 'venue';

/** Asks the server which role the stored secret has. Throws AdminAuthError when there is none or it is wrong. */
export async function fetchAdminRole(): Promise<AdminRole> {
  const res = await adminFetch('/admin/whoami');
  if (!res.ok) throw new Error('Could not check your admin access.');
  const role = ((await res.json()) as { role?: string }).role;
  return role === 'venue' ? 'venue' : 'owner';
}

/** Whether the Leaderboard option is switched on for every player's device. */
export async function fetchLeaderboardEnabled(): Promise<boolean> {
  const res = await adminFetch('/admin/leaderboard-enabled');
  if (!res.ok) throw new Error('Could not check whether leaderboard mode is on.');
  return ((await res.json()) as { enabled: boolean }).enabled === true;
}

/** Throws with a readable message when the change could not be saved; returns the setting as saved. */
export async function saveLeaderboardEnabledApi(enabled: boolean): Promise<boolean> {
  const res = await adminFetch('/admin/leaderboard-enabled', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  const body = (await res.json().catch(() => ({}))) as { enabled?: boolean; error?: string };
  if (!res.ok) throw new Error(body.error ?? 'Could not change leaderboard mode.');
  return body.enabled === true;
}

export async function kickSession(sessionId: string): Promise<void> {
  await adminFetch('/admin/kick', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
}

export async function kickAndBlockSession(sessionId: string): Promise<void> {
  await adminFetch('/admin/kick-and-block', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
}

export async function revokeAllSessionsApi(): Promise<void> {
  await adminFetch('/admin/revoke-all', { method: 'POST' });
}

export async function fetchVenues(): Promise<Venue[]> {
  const res = await adminFetch('/admin/venues');
  const body = (await res.json()) as { venues: Venue[] };
  return body.venues;
}

/** Throws with the server's validation message on a rejected save (e.g. a bad coordinate) — the only admin call that can fail on bad input rather than just auth. */
export async function saveVenuesApi(venues: Venue[]): Promise<Venue[]> {
  const res = await adminFetch('/admin/venues', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ venues }),
  });
  const body = (await res.json()) as { venues: Venue[] } | { error: string };
  if (!res.ok) throw new Error('error' in body ? body.error : 'Could not save venues.');
  return (body as { venues: Venue[] }).venues;
}

/** `undefined` means nothing has been saved server-side yet — caller decides the fallback. */
export async function fetchGlobalBranding(): Promise<Branding | undefined> {
  const res = await adminFetch('/admin/branding');
  const body = (await res.json()) as { branding: Branding | null };
  return body.branding ?? undefined;
}

/** Throws with the server's validation message on a rejected save (e.g. an empty name). */
export async function saveGlobalBrandingApi(branding: Branding): Promise<Branding> {
  const res = await adminFetch('/admin/branding', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ branding }),
  });
  const body = (await res.json()) as { branding: Branding } | { error: string };
  if (!res.ok) throw new Error('error' in body ? body.error : 'Could not save branding.');
  return (body as { branding: Branding }).branding;
}

// ---------------------------------------------------------------------------
// Venue leaderboard admin — see relay/leaderboard.ts for what each call does
// ---------------------------------------------------------------------------

/** 'zero_sum' = Serious-style (points sum to zero); 'winner_only' = Fun-style (nobody negative). */
export type Settlement = 'zero_sum' | 'winner_only';
export type LeaderboardPeriodStatus = 'upcoming' | 'running' | 'ended';

export interface LeaderboardPeriod {
  id: string;
  venue: string;
  startsAt: number;
  endsAt: number;
  minRounds: number;
  settlement: Settlement;
  maxGamesPerDay: number;
  maxGamesPerPeriod: number | null;
  minSecondsPerRound: number;
  /** The frozen house rules: `{ mode, toggles, serious, fun }` (see leaderboardAdmin.ts's `periodRulesBlob`). */
  rules: unknown;
  status: LeaderboardPeriodStatus;
  gamesCompleted: number;
  gamesHidden: number;
  players: number;
}

export interface CreateLeaderboardPeriodInput {
  venue: string;
  startsAt: number;
  days: number;
  minRounds: number;
  settlement: Settlement;
  maxGamesPerDay: number;
  /** Omit for no period-wide limit. */
  maxGamesPerPeriod?: number;
  minSecondsPerRound: number;
  rules: unknown;
}

export interface ReviewFlag {
  code: 'fast' | 'repeat_table' | 'one_sided' | 'new_accounts' | 'device_overlap' | 'outlier';
  /** 'warn' = a genuine game rarely does this; 'info' = common in real play, only context. */
  severity: 'warn' | 'info';
  message: string;
}

export interface ReviewSeat {
  nickname: string;
  points: number | null;
  wins: number | null;
  /** False = this player was already at their game limit: stored, but not on the board. */
  counted: boolean;
  createdAt: number;
}

export interface ReviewGameItem {
  id: string;
  startedAt: number;
  completedAt: number;
  durationMs: number;
  rounds: number | null;
  deviceId: string | null;
  hidden: boolean;
  hiddenReason: string | null;
  hiddenAt: number | null;
  players: ReviewSeat[];
  flags: ReviewFlag[];
}

export interface PeriodReviewData {
  period: Omit<LeaderboardPeriod, 'status' | 'gamesCompleted' | 'gamesHidden' | 'players'>;
  games: ReviewGameItem[];
  summary: { games: number; flagged: number; hidden: number };
}

export interface LeaderboardPlayerItem {
  nickname: string;
  createdAt: number;
  locked: boolean;
  /** 'pending' = asked to join and is waiting for staff. */
  status: 'pending' | 'approved';
}

/** POSTs JSON to an admin route; throws the server's own message when it refuses (e.g. "Give a short reason…"). */
async function adminAction(path: string, body: unknown, method = 'POST'): Promise<Record<string, unknown>> {
  const res = await adminFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error(typeof parsed.error === 'string' ? parsed.error : 'The server refused that.');
  return parsed;
}

export async function fetchLeaderboardPeriods(venue?: string): Promise<LeaderboardPeriod[]> {
  const res = await adminFetch(`/admin/leaderboard/periods${venue ? `?venue=${encodeURIComponent(venue)}` : ''}`);
  return ((await res.json()) as { periods: LeaderboardPeriod[] }).periods;
}

/** Throws the server's validation message (e.g. an overlapping period). */
export async function createLeaderboardPeriod(input: CreateLeaderboardPeriodInput): Promise<LeaderboardPeriod> {
  return (await adminAction('/admin/leaderboard/period', input, 'PUT')).period as LeaderboardPeriod;
}

/** Only a period that has not started and has no games can be deleted — otherwise throws why not. */
export async function deleteLeaderboardPeriod(periodId: string): Promise<void> {
  await adminAction('/admin/leaderboard/period/delete', { periodId });
}

export async function fetchPeriodReview(periodId: string): Promise<PeriodReviewData> {
  const res = await adminFetch(`/admin/leaderboard/review?period=${encodeURIComponent(periodId)}`);
  const body = (await res.json()) as PeriodReviewData | { error: string };
  if (!res.ok) throw new Error('error' in body ? body.error : 'Could not load that period.');
  return body as PeriodReviewData;
}

export async function hideLeaderboardGame(gameId: string, reason: string): Promise<void> {
  await adminAction('/admin/leaderboard/game/hide', { gameId, reason });
}

export async function restoreLeaderboardGame(gameId: string): Promise<void> {
  await adminAction('/admin/leaderboard/game/restore', { gameId });
}

export async function fetchLeaderboardPlayers(opts: { query?: string; lockedOnly?: boolean } = {}): Promise<LeaderboardPlayerItem[]> {
  const params = new URLSearchParams();
  if (opts.query) params.set('q', opts.query);
  if (opts.lockedOnly) params.set('locked', '1');
  const res = await adminFetch(`/admin/leaderboard/players${params.size ? `?${params}` : ''}`);
  return ((await res.json()) as { players: LeaderboardPlayerItem[] }).players;
}

/** Returns the nickname as registered (its capitalisation), which may differ from what was typed. */
export async function unlockLeaderboardPlayer(nickname: string): Promise<string> {
  return (await adminAction('/admin/leaderboard/player/unlock', { nickname })).nickname as string;
}

/** Someone who asked to join and is waiting for staff. */
export interface JoinRequest {
  nickname: string;
  requestedAt: number;
}

export async function fetchJoinRequests(): Promise<JoinRequest[]> {
  const res = await adminFetch('/admin/leaderboard/requests');
  if (!res.ok) throw new Error('Could not load the join requests.');
  return ((await res.json()) as { requests: JoinRequest[] }).requests;
}

export async function approveJoinRequest(nickname: string): Promise<string> {
  return (await adminAction('/admin/leaderboard/request/approve', { nickname })).nickname as string;
}

export async function declineJoinRequest(nickname: string): Promise<string> {
  return (await adminAction('/admin/leaderboard/request/decline', { nickname })).nickname as string;
}

/** A table the host has sent to the staff, waiting to be approved (or declined). */
export interface TableRequest {
  ref: string;
  tableName: string;
  /** The venue, from where the host's phone was when the table opened. */
  venue: string;
  requestedAt: number;
  /** The four User IDs in seating order (in the order they joined when the dice decide the seats). */
  players: string[];
  seatingMode: 'entered' | 'dice';
}

export async function fetchTableRequests(): Promise<TableRequest[]> {
  const res = await adminFetch('/admin/leaderboard/tables');
  if (!res.ok) throw new Error('Could not load the tables waiting for approval.');
  return ((await res.json()) as { tables: TableRequest[] }).tables;
}

/** Returns the table's name. Refused (with the reason) if it is no longer waiting or a player can no longer sit. */
export async function approveTable(ref: string): Promise<string> {
  return (await adminAction('/admin/leaderboard/table/approve', { ref })).tableName as string;
}

export async function declineTable(ref: string): Promise<string> {
  return (await adminAction('/admin/leaderboard/table/decline', { ref })).tableName as string;
}

/** One venue's running period with its standings, as the staff screens show it. */
export interface StandingsBoard {
  venue: string;
  period: LeaderboardPeriodInfo;
  entries: LeaderboardEntry[];
}

/** The standings of every running period (owner and venue staff). */
export async function fetchStandings(): Promise<StandingsBoard[]> {
  const res = await adminFetch('/admin/leaderboard/standings');
  if (!res.ok) throw new Error('Could not load the leaderboard.');
  return ((await res.json()) as { boards: StandingsBoard[] }).boards;
}

/** Whether every table must be approved by staff before it plays (owner only). */
export async function fetchTableApprovalRequired(): Promise<boolean> {
  const res = await adminFetch('/admin/leaderboard-table-approval');
  if (!res.ok) throw new Error('Could not check the table approval setting.');
  return ((await res.json()) as { required: boolean }).required === true;
}

export async function saveTableApprovalRequiredApi(required: boolean): Promise<boolean> {
  const res = await adminFetch('/admin/leaderboard-table-approval', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ required }),
  });
  const body = (await res.json().catch(() => ({}))) as { required?: boolean; error?: string };
  if (!res.ok) throw new Error(body.error ?? 'Could not change the table approval setting.');
  return body.required === true;
}

/** Adds a walk-up player who is approved straight away with the standard temporary PIN; they choose their own when they first play. */
export async function addLeaderboardPlayer(nickname: string): Promise<string> {
  return (await adminAction('/admin/leaderboard/player/add', { nickname })).nickname as string;
}

/** Whether new players must be approved by staff before they can play (owner only). */
export async function fetchApprovalRequired(): Promise<boolean> {
  const res = await adminFetch('/admin/leaderboard-approval');
  if (!res.ok) throw new Error('Could not check the approval setting.');
  return ((await res.json()) as { required: boolean }).required === true;
}

export async function saveApprovalRequiredApi(required: boolean): Promise<boolean> {
  const res = await adminFetch('/admin/leaderboard-approval', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ required }),
  });
  const body = (await res.json().catch(() => ({}))) as { required?: boolean; error?: string };
  if (!res.ok) throw new Error(body.error ?? 'Could not change the approval setting.');
  return body.required === true;
}

/** Whether a venue-admin secret exists (owner only). The secret itself is never readable again. */
export async function fetchVenueAdminConfigured(): Promise<boolean> {
  const res = await adminFetch('/admin/venue-admin');
  if (!res.ok) throw new Error('Could not check venue admin access.');
  return ((await res.json()) as { configured: boolean }).configured === true;
}

/** Makes a new venue-admin secret (replacing any earlier one) and returns it — this is the only time it is shown. */
export async function generateVenueAdminSecretApi(): Promise<string> {
  const res = await adminFetch('/admin/venue-admin/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const body = (await res.json().catch(() => ({}))) as { secret?: string; error?: string };
  if (!res.ok || !body.secret) throw new Error(body.error ?? 'Could not create the venue admin access.');
  return body.secret;
}

export async function revokeVenueAdminApi(): Promise<void> {
  const res = await adminFetch('/admin/venue-admin/revoke', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) throw new Error('Could not remove the venue admin access.');
}

// --- the board display (TV link, screen settings, season state) — owner and venue staff -----------------------------

export interface DisplayAdminSettings {
  screenSeconds: number;
  showTable: boolean;
  showPodium: boolean;
  /** One of the app's theme ids, or null for the default. Only the owner can change it (and logoPlate). */
  theme: string | null;
  logoPlate: boolean;
}

export interface TvLinkInfo {
  token: string;
  venue: string;
}

export interface DisplayAdminData {
  settings: DisplayAdminSettings;
  link: TvLinkInfo | null;
  /** The venues a TV link can be made for. */
  venues: string[];
}

export async function fetchDisplayAdmin(): Promise<DisplayAdminData> {
  const res = await adminFetch('/admin/leaderboard/display');
  const body = (await res.json().catch(() => undefined)) as Partial<DisplayAdminData> | undefined;
  if (!res.ok || !body?.settings || !Array.isArray(body.venues)) throw new Error('Could not load the display settings.');
  return { settings: body.settings, link: body.link ?? null, venues: body.venues };
}

/** Throws the server's message when it refuses (e.g. seconds out of range). Venue staff's theme/plate fields are ignored by the server. */
export async function saveDisplayAdmin(input: DisplayAdminSettings): Promise<DisplayAdminSettings> {
  return (await adminAction('/admin/leaderboard/display', input, 'PUT')).settings as DisplayAdminSettings;
}

/** Makes (or replaces) the TV link for a venue; the old address stops working at once. */
export async function createTvLinkApi(venue: string): Promise<TvLinkInfo> {
  return (await adminAction('/admin/leaderboard/display/link', { venue })).link as TvLinkInfo;
}

export async function removeTvLinkApi(): Promise<void> {
  await adminAction('/admin/leaderboard/display/link/remove', {});
}

/** A finished season that still has results; `review` until staff mark it final. */
export interface SeasonItem {
  id: string;
  venue: string;
  season: number;
  startsAt: number;
  endsAt: number;
  state: 'review' | 'final';
  players: number;
}

export async function fetchSeasons(): Promise<SeasonItem[]> {
  const res = await adminFetch('/admin/leaderboard/seasons');
  const body = (await res.json().catch(() => undefined)) as { seasons?: SeasonItem[] } | undefined;
  if (!res.ok || !Array.isArray(body?.seasons)) throw new Error('Could not load the finished seasons.');
  return body.seasons;
}

/** Marks a finished season final (true) or sends it back to review (false). Throws the server's reason (e.g. not ended yet). */
export async function setSeasonFinal(periodId: string, final: boolean): Promise<void> {
  await adminAction('/admin/leaderboard/season/final', { periodId, final });
}

/** Deletes a player and their leaderboard data on request. Throws with the server's reason (e.g. still in a game). */
export async function deleteLeaderboardPlayer(nickname: string): Promise<string> {
  return (await adminAction('/admin/leaderboard/player/delete', { nickname })).nickname as string;
}

